<template>
  <div v-if="dataStore.contact.ira_questionnaire">
    <!-- Disclaimer -->
    <div class="bg-container-bg p-4 rounded-xl">
      <AsteriskNotice text="Important Disclaimer" />
      <p class="font-medium text-strongest font-inter mt-2">
        To help us assist you in completing your IRA investment, please complete this brief
        questionnaire.
      </p>
    </div>

    <!-- Questionnaire -->
    <div class="mt-10">
      <h3 class="text-2xl font-semibold text-strongest mb-4">IRA questionnaire</h3>

      <div class="flex flex-col gap-4 text-strongest">
        <!-- Question 1: Investment Type. Options span the full card width rather than sitting in
             the right-hand column like questions 2-4 — there are enough of them, with long
             enough names, that the narrower column would wrap every other label. -->
        <div class="rounded-xl p-5 bg-container-bg">
          <h3 class="font-semibold font-inter mb-3">1. Select investment type</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-strongest">
            <label
              v-for="option in investmentTypeOptions"
              :key="option"
              class="relative"
              :class="
                isInvestmentTypeLocked && option !== purchaseFlowStore.selected_rollover.type
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              "
            >
              <input
                type="radio"
                name="type_of_ira"
                :value="option"
                v-model="purchaseFlowStore.current_investment.type"
                class="sr-only"
                :disabled="
                  isInvestmentTypeLocked && option !== purchaseFlowStore.selected_rollover.type
                "
              />
              <div
                class="flex items-center gap-3 py-3 px-2 rounded-xl h-full"
                :class="
                  purchaseFlowStore.current_investment.type === option
                    ? 'bg-radio-checkbox-selector'
                    : ''
                "
              >
                <div
                  class="min-w-4 min-h-4 h-4 w-4 rounded-full border border-radio-checkbox-outline flex items-center justify-center"
                  :class="
                    purchaseFlowStore.current_investment.type === option
                      ? '!border-4 !border-radio-checkbox-checked'
                      : ''
                  "
                ></div>
                <p class="text-sm font-medium font-inter md:text-base">{{ option }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- Questions 2-4: Yes/No Radio Questions -->
        <div
          v-for="(field, index) in yesNoQuestions"
          :key="field.field_name"
          class="rounded-xl p-5 bg-container-bg grid grid-cols-1 md:grid-cols-2 gap-4 items-center"
        >
          <h3 class="font-semibold font-inter">{{ index + 2 }}. {{ field.question }}</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-strongest">
            <label
              v-for="option in field.options"
              :key="option.label"
              class="relative cursor-pointer"
            >
              <input
                type="radio"
                :name="field.field_name"
                :value="option.value"
                v-model="dataStore.contact.ira_questionnaire[field.field_name]"
                class="sr-only"
              />
              <div
                class="flex items-center gap-3 py-3 px-2 rounded-xl"
                :class="
                  dataStore.contact.ira_questionnaire[field.field_name] === option.value
                    ? 'bg-radio-checkbox-selector'
                    : ''
                "
              >
                <div
                  class="min-w-4 min-h-4 h-4 w-4 rounded-full border border-radio-checkbox-outline flex items-center justify-center"
                  :class="
                    dataStore.contact.ira_questionnaire[field.field_name] === option.value
                      ? '!border-4 !border-radio-checkbox-checked'
                      : ''
                  "
                ></div>
                <p class="text-sm font-medium font-inter md:text-base">{{ option.label }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- Question 5: Custodian (conditional on Q2 = Yes) -->
        <div
          v-if="dataStore.contact.ira_questionnaire.has_ira_account === true"
          class="rounded-xl p-5 bg-container-bg grid grid-cols-1 md:grid-cols-2 gap-4 items-center animate-slideup"
        >
          <h3 class="font-semibold font-inter">
            5. Because you answered "Yes" to Question 2, please select your current custodian
          </h3>
          <div class="flex flex-col gap-3">
            <!-- `label` isn't decorative here: BaseInput's dropdown chevron is positioned for
                 the standard label-above-value layout, so a label-less dropdown sits high. -->
            <BaseInput
              label="Custodian"
              field_name="custodian"
              field_type="dropdown"
              place_holder="Select custodian"
              is_required
              dropdown_above
              :dropdown_options="custodianOptions"
              v-model="custodian_selection"
              @update:modelValue="handleCustodianSelection"
            />
            <!-- The "not listed" escape hatch, so the step is never a dead end for an
                 investor whose custodian isn't one of the options. -->
            <BaseInput
              v-if="isCustodianOther"
              label="Where are my retirement funds held?"
              field_name="retirement_funds_held_at"
              field_type="string"
              place_holder="Enter custodian or institution name"
              is_required
              v-model="dataStore.contact.ira_questionnaire.retirement_funds_held_at"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { BaseInput, AsteriskNotice } from "@/components";
import { usePurchaseFlowStore, useDataStore, useUIStore } from "@/stores";
import { useStripeCheckout } from "@/composables/useStripeCheckout";
import {
  IRA_INVESTMENT_TYPES,
  IRA_CUSTODIAN_OPTIONS,
  CUSTODIAN_NOT_LISTED_OPTION,
} from "@/constants";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();
const UIStore = useUIStore();
const stripeCheckout = useStripeCheckout();

onMounted(() => {
  if (!dataStore.contact.ira_questionnaire) {
    dataStore.contact.ira_questionnaire = {
      has_ira_account: false,
      in_self_directed_ira: false,
      has_other_qualified_funds: false,
      custodian: "",
      // Free-text institution name, only used when `custodian` is CUSTODIAN_NOT_LISTED_OPTION.
      retirement_funds_held_at: "",
    };
  }

  // IRA investments must use Wire. Cancel any active card hold before forcing the switch.
  const inv = purchaseFlowStore.current_investment;
  if (inv.payment_method === "Card") {
    const piId = String(inv.funding_account ?? "").startsWith("pi_") ? inv.funding_account : null;
    stripeCheckout.cancelPaymentIntent(piId);
    inv.funding_account = null;
  }

  inv.payment_method = "Wire";
  inv.distribution_method = "Check";
  inv.distribution_account = null;
  inv.funding_account = null;

  syncCustodianSelection();
});

const investmentTypeOptions = computed(() => {
  const options = [...IRA_INVESTMENT_TYPES];
  // A rollover locks the type to the parent investment's, which can be a rarer type that
  // isn't in the standard list (Solo 401(k), Trust Traditional IRA, ...). Keep whatever is
  // already on the investment selectable so the dropdown can render it.
  const current = purchaseFlowStore.current_investment?.type;
  if (current && !options.includes(current)) options.unshift(current);
  return options;
});

/* Custodian selection */
const custodian_selection = ref("");

const custodianOptions = [...IRA_CUSTODIAN_OPTIONS, CUSTODIAN_NOT_LISTED_OPTION];

const isCustodianOther = computed(
  () => custodian_selection.value === CUSTODIAN_NOT_LISTED_OPTION
);

const matchingCustodianOption = (value) =>
  IRA_CUSTODIAN_OPTIONS.find(
    (name) => name.toLowerCase() === (value || "").trim().toLowerCase()
  );

function syncCustodianSelection() {
  const ira_questionnaire = dataStore.contact.ira_questionnaire;
  const saved = (ira_questionnaire?.custodian || "").trim();

  if (!saved) {
    custodian_selection.value = "";
    return;
  }

  const match = matchingCustodianOption(saved);

  // `custodian` used to be free text, so saved answers arrive in whatever casing the
  // investor typed ("fidelity"). Store the canonical spelling once it matches an option.
  if (match) {
    ira_questionnaire.custodian = match;
    custodian_selection.value = match;
    return;
  }

  // Anything else is a legacy free-text answer. Move it to the field it now belongs in
  // rather than losing it, and record the selection they would have to make today.
  if (saved !== CUSTODIAN_NOT_LISTED_OPTION) {
    ira_questionnaire.retirement_funds_held_at =
      ira_questionnaire.retirement_funds_held_at || saved;
    ira_questionnaire.custodian = CUSTODIAN_NOT_LISTED_OPTION;
  }

  custodian_selection.value = CUSTODIAN_NOT_LISTED_OPTION;
}

function handleCustodianSelection(value) {
  const ira_questionnaire = dataStore.contact.ira_questionnaire;

  ira_questionnaire.custodian = value;

  // A real custodian off the list makes any previously typed institution name stale.
  if (value !== CUSTODIAN_NOT_LISTED_OPTION) {
    ira_questionnaire.retirement_funds_held_at = "";
  }
}

const yesNoQuestions = [
  {
    question: "Do you currently have an IRA account?",
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
    field_name: "has_ira_account",
  },
  {
    question: "Are you in a self-directed IRA?",
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
    field_name: "in_self_directed_ira",
  },
  {
    question:
      "Do you have other Qualified funds you wish to invest (i.e. 401k from a previous employer, etc.)",
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
    field_name: "has_other_qualified_funds",
  },
];

const isInvestmentTypeLocked = computed(() => !!purchaseFlowStore.selected_rollover);

defineExpose({
  validate() {
    const ira_questionnaire = dataStore.contact.ira_questionnaire;

    if (ira_questionnaire.custodian) {
      ira_questionnaire.custodian = ira_questionnaire.custodian.trim();
    }
    if (ira_questionnaire.retirement_funds_held_at) {
      ira_questionnaire.retirement_funds_held_at =
        ira_questionnaire.retirement_funds_held_at.trim();
    }

    if (ira_questionnaire.has_ira_account === true) {
      let message = "";
      if (!ira_questionnaire.custodian) {
        message = "Please select your current custodian.";
      } else if (isCustodianOther.value && !ira_questionnaire.retirement_funds_held_at) {
        // "My custodian isn't listed" is a non-empty answer but doesn't name a custodian,
        // so the typed institution name is what has to be there.
        message = "Please enter where your retirement funds are currently held.";
      }

      if (message) {
        UIStore.animateNotificationAlert({ type: "error", message });
        return { proceed: false, error_messages: [message] };
      }
    }
    return { proceed: true };
  },
  afterContinue: async () => {
    const promises = [
      purchaseFlowStore.saveOwnerProgress(),
      purchaseFlowStore.saveInvestmentProgress(),
    ];

    await Promise.all(promises);
  },
});
</script>
